import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowRight, Upload, Plus, Trash2, FileText, X, FilePlus2 } from "lucide-react";
import OnboardingLayout from "@/components/OnboardingLayout";
import { useBusiness } from "@/context/BusinessContext";
import { api, apiError } from "@/lib/api";
import { Button, Input, Spinner, Skeleton } from "@/components/ui";

const md = {
  h1: ({ node, ...p }) => <h3 className="text-sm font-semibold mt-3 mb-1" style={{ color: "var(--text)" }} {...p} />,
  h2: ({ node, ...p }) => <h3 className="text-sm font-semibold mt-3 mb-1" style={{ color: "var(--text)" }} {...p} />,
  h3: ({ node, ...p }) => <h4 className="text-sm font-semibold mt-3 mb-1" style={{ color: "var(--text)" }} {...p} />,
  p: ({ node, ...p }) => <p className="text-sm my-1.5" style={{ color: "var(--text-2)", lineHeight: 1.65 }} {...p} />,
  ul: ({ node, ...p }) => <ul className="list-disc pl-5 space-y-1 my-1.5 text-sm" style={{ color: "var(--text-2)" }} {...p} />,
  ol: ({ node, ...p }) => <ol className="list-decimal pl-5 space-y-1 my-1.5 text-sm" style={{ color: "var(--text-2)" }} {...p} />,
  strong: ({ node, ...p }) => <strong style={{ color: "var(--text)" }} {...p} />,
};

export default function OnboardingKnowledge() {
  const { active, loading } = useBusiness();
  const nav = useNavigate();
  const [docs, setDocs] = React.useState([]);
  const [title, setTitle] = React.useState("");
  const [content, setContent] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  const [viewing, setViewing] = React.useState(false);
  const [docsLoading, setDocsLoading] = React.useState(true);
  const fileRef = React.useRef();

  const load = React.useCallback(async () => {
    if (!active) return;
    try {
      const { data } = await api.get(`/businesses/${active.id}/knowledge`);
      setDocs(data);
    } finally { setDocsLoading(false); }
  }, [active]);

  React.useEffect(() => {
    if (!loading && !active) nav("/onboarding/business", { replace: true });
    else if (active) load();
  }, [loading, active, nav, load]);

  if (loading || !active) return null;

  const addText = async () => {
    if (!title.trim() || !content.trim()) return;
    setBusy(true);
    try {
      await api.post(`/businesses/${active.id}/knowledge/text`, { title, content });
      setTitle(""); setContent(""); await load(); toast.success("Added to knowledge");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/businesses/${active.id}/knowledge/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load(); toast.success(`Added ${file.name}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/businesses/${active.id}/knowledge/${id}`);
      if (selected?.id === id) setSelected(null);
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const viewDoc = async (id) => {
    setViewing(true); setSelected({ id, title: "", text: "" });
    try {
      const { data } = await api.get(`/businesses/${active.id}/knowledge/${id}`);
      setSelected(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setSelected(null); }
    finally { setViewing(false); }
  };

  const docSidebar = (
    <div data-testid="kb-doc-rail">
      <button className={`nav-link w-full text-left ${!selected ? "active" : ""}`} onClick={() => setSelected(null)} data-testid="kb-new-entry">
        <FilePlus2 size={16} strokeWidth={1.6} style={{ flexShrink: 0 }} />
        <span className="flex-1">New entry</span>
        {!selected && <span className="mono text-[10px]" style={{ color: "var(--text-muted)" }}>editing</span>}
      </button>

      {docsLoading ? (
        [0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-2 px-3 py-2.5">
            <Skeleton style={{ width: 16, height: 16 }} />
            <Skeleton className="flex-1" style={{ height: 12, maxWidth: 120 }} />
          </div>
        ))
      ) : docs.length === 0 ? (
        <p className="px-4 pt-2 text-xs" style={{ color: "var(--text-muted)" }}>No saved entries yet.</p>
      ) : (
        docs.map((d) => {
          const isSel = selected?.id === d.id;
          return (
            <div key={d.id} className="group relative">
              <button className={`nav-link w-full text-left ${isSel ? "active" : ""}`} onClick={() => viewDoc(d.id)} data-testid={`kb-doc-${d.id}`}>
                <FileText size={16} strokeWidth={1.6} style={{ flexShrink: 0 }} />
                <span className="truncate flex-1 pr-5">{d.title}</span>
              </button>
              <button onClick={(e) => { e.stopPropagation(); remove(d.id); }} title="Delete" data-testid={`kb-delete-${d.id}`}
                className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "var(--text-muted)" }}>
                <Trash2 size={13} />
              </button>
            </div>
          );
        })
      )}
    </div>
  );

  return (
    <OnboardingLayout current="knowledge" title="Add your info & FAQs" sidebar={docSidebar}
      secondaryAction={{ label: "Skip for now", onClick: () => nav("/onboarding/integrations"), variant: "ghost", testId: "kb-skip" }}
      primaryAction={{ label: "Continue", onClick: () => nav("/onboarding/integrations"), testId: "kb-continue", icon: <ArrowRight size={16} /> }}>
      {selected ? (
        <div className="w-full" data-testid="kb-doc-view">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 min-w-0">
              <FileText size={16} style={{ color: "var(--accent-2)" }} />
              <span className="text-base truncate" style={{ color: "var(--text)" }}>{selected.title || "Loading…"}</span>
            </div>
            <button onClick={() => setSelected(null)} data-testid="kb-doc-close" className="flex items-center gap-1 text-sm" style={{ color: "var(--text-muted)" }}>
              <X size={15} /> Close
            </button>
          </div>
          {viewing ? <Spinner size={18} /> : (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={md}>{selected.text || "_(empty)_"}</ReactMarkdown>
          )}
        </div>
      ) : (
        <div className="w-full max-w-2xl">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title — e.g. Pricing" data-testid="kb-title" className="text-base mb-4" />

          <textarea value={content} onChange={(e) => setContent(e.target.value)} data-testid="kb-content" spellCheck={false}
            placeholder={"## Pricing\n- A standard 2-bed clean is **$120**\n- Takes about 3 hours\n- We accept cash and card"}
            className="w-full mono text-sm"
            style={{ background: "var(--s1)", color: "var(--text)", border: "none", outline: "none", resize: "vertical", minHeight: 300, padding: "14px 16px", lineHeight: 1.7 }} />

          <div className="flex items-center gap-4 mt-5">
            <Button variant="accent2" onClick={addText} disabled={busy || !title.trim() || !content.trim()} data-testid="kb-add-text">
              {busy ? <Spinner size={14} /> : <Plus size={15} />} Add to knowledge
            </Button>
            <span className="text-sm" style={{ color: "var(--text-muted)" }}>or</span>
            <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md,.csv" onChange={onFile} hidden data-testid="kb-file-input" />
            <Button variant="ghost" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="kb-upload-btn">
              {uploading ? <Spinner size={14} /> : <Upload size={15} />} Upload a file
            </Button>
          </div>
        </div>
      )}
    </OnboardingLayout>
  );
}
